import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Alert,
  Paper
} from '@mui/material';
import { InfoOutlined, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useCrmSettings } from 'src/contexts/SettingContext';

const SeoTab = ({ form, handleInput, setField, cities, states, basicTabData }) => {
  // console.log('User left Basic Details tab. Current data:', basicTabData);
  // console.log(' State', basicTabData?.state_name);
  // console.log('✅City :', basicTabData?.city_name);
  // console.log('✅title', basicTabData?.title);
  // console.log('✅Description', basicTabData?.description);

  const { getSettingByKey, loading: settingsLoading } = useCrmSettings();
  const [slug, setSlug] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [isDefaultDescription, setIsDefaultDescription] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!settingsLoading) {
      const companyNameFromApi = getSettingByKey('company_name');
      setCompanyName(companyNameFromApi || process.env.REACT_APP_COMPANY_NAME || 'E-state');
    }
  }, [settingsLoading, getSettingByKey]);

  const generateSlug = () => {
    if (!basicTabData?.title) return '';

    let baseSlug = basicTabData.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();

    let stateSlug = '';
    const selectedState = states.find((s) => String(s.id) === String(basicTabData?.state));

    if (selectedState?.abbreviation) {
      stateSlug = selectedState.abbreviation.toLowerCase();
    } else if (basicTabData?.state_name) {
      stateSlug = basicTabData.state_name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
    }

    if (stateSlug) baseSlug += `-${stateSlug}`;

    let citySlug = '';
    const selectedCity = cities.find((c) => String(c.id) === String(basicTabData?.city));

    if (selectedCity?.name) {
      citySlug = selectedCity.name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
    } else if (basicTabData?.city_name) {
      citySlug = basicTabData.city_name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
    }

    if (citySlug) baseSlug += `-${citySlug}`;

    return baseSlug;
  };

  const generateMetaTitle = () => {
    if (!basicTabData?.title) return '';
    const titleCase = (str) =>
      str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    return `${titleCase(basicTabData.title)} | ${companyName}`;
  };

  const generateMetaDescription = () => {
    if (!basicTabData?.description) return '';

    const cleaned = basicTabData.description.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();

    return cleaned.length > 120 ? cleaned.substring(0, 120) + '' : cleaned;
  };

  useEffect(() => {
    if (form.slug || form.meta_title || form.meta_description) return;
    if (
      basicTabData?.title ||
      basicTabData?.description ||
      basicTabData?.city ||
      basicTabData?.state
    ) {
      setSlug(generateSlug());
      setMetaTitle(generateMetaTitle());
      setMetaDescription(generateMetaDescription());
      setIsDefaultDescription(true);
    }
  }, [
    basicTabData?.title,
    basicTabData?.description,
    basicTabData?.city,
    basicTabData?.state,
    cities,
    states
  ]);

  useEffect(() => {
    if (initialLoad) {
      if (form.slug) setSlug(form.slug);
      if (form.meta_title) setMetaTitle(form.meta_title);
      if (form.meta_description) {
        setMetaDescription(form.meta_description);
        setIsDefaultDescription(false);
      }
      setInitialLoad(false);
    }
  }, [form.slug, form.meta_title, form.meta_description, initialLoad]);

  useEffect(() => {
    if (!initialLoad) {
      if (!slug && basicTabData?.title && basicTabData?.state && basicTabData?.city)
        setSlug(generateSlug());
      if (!metaTitle && basicTabData?.title) setMetaTitle(generateMetaTitle());
      if (!metaDescription && basicTabData?.description) {
        setMetaDescription(generateMetaDescription());
        setIsDefaultDescription(true);
      }
    }
  }, [
    basicTabData?.title,
    basicTabData?.description,
    basicTabData?.city,
    basicTabData?.state,
    cities,
    states,
    initialLoad
  ]);

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9\s-]/g, '');
    setSlug(value);
  };

  const handleMetaTitleChange = (e) => setMetaTitle(e.target.value);

  const handleMetaDescriptionChange = (e) => {
    setMetaDescription(e.target.value);
    setIsDefaultDescription(false);
  };

  useEffect(() => {
    setField('slug', slug);
  }, [slug]);

  useEffect(() => {
    setField('metaTitle', metaTitle);
  }, [metaTitle]);

  useEffect(() => {
    setField('metaDescription', metaDescription);
  }, [metaDescription]);

  const metaTitleLength = metaTitle.length;
  const metaDescriptionLength = metaDescription.length;

  const getMetaTitleStatus = () => {
    if (metaTitleLength === 0) return 'empty';
    if (metaTitleLength < 50) return 'too-short';
    if (metaTitleLength > 60) return 'too-long';
    return 'ideal';
  };

  const getMetaDescriptionStatus = () => {
    if (metaDescriptionLength === 0) return 'empty';
    if (metaDescriptionLength < 120) return 'too-short';
    if (metaDescriptionLength > 160) return 'too-long';
    return 'ideal';
  };

  const metaTitleStatus = getMetaTitleStatus();
  const metaDescriptionStatus = getMetaDescriptionStatus();

  return (
    <Box component="form" noValidate>
      <Grid container spacing={3}>
        {/* SLUG FIELD */}
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="slug-field">Slug *</InputLabel>
            <OutlinedInput
              id="slug-field"
              value={slug}
              onChange={handleSlugChange}
              label="Slug *"
              endAdornment={
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    {slug.length} chars
                  </Typography>
                </InputAdornment>
              }
            />
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            • Must be lowercase only • Use hyphens between every word • No special characters • Must
            include city + state abbreviation
          </Typography>
        </Grid>

        {/* SEO PREVIEW */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2, bgcolor: '#f8f9fa' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <InfoOutlined color="primary" />
              Slug Preview
            </Typography>

            {/* <Typography variant="body2" color="primary" sx={{ mb: 1 }} noWrap>
              https://{window.location.hostname}/property-detail/
              {slug || 'your-property-slug'}
            </Typography> */}

            <Typography variant="body2" color="primary" sx={{ mb: 1 }} noWrap>
              <a
                // href={`https://${window.location.hostname}/property-detail/${
                //   slug || 'your-property-slug'
                // }`}
                href={`https://web.leadlockllc.com/property-detail/${slug || 'your-property-slug'}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                {/* https://{window.location.hostname}/property-detail/{slug || 'your-property-slug'} */}
                https://web.leadlockllc.com/property-detail/{slug || 'your-property-slug'}
              </a>
            </Typography>
          </Paper>
        </Grid>

        {/* META TITLE */}
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="meta-title-field">Meta Title *</InputLabel>
            <OutlinedInput
              id="meta-title-field"
              value={metaTitle}
              onChange={handleMetaTitleChange}
              label="Meta Title *"
              endAdornment={
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {metaTitleStatus === 'ideal' && (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                    {metaTitleStatus === 'too-short' && (
                      <ErrorIcon color="warning" fontSize="small" />
                    )}
                    {metaTitleStatus === 'too-long' && <ErrorIcon color="error" fontSize="small" />}
                    <Typography
                      variant="caption"
                      color={
                        metaTitleStatus === 'ideal'
                          ? 'success.main'
                          : metaTitleStatus === 'too-short'
                          ? 'warning.main'
                          : metaTitleStatus === 'too-long'
                          ? 'error.main'
                          : 'text.secondary'
                      }
                    >
                      {metaTitleLength}/60
                    </Typography>
                  </Box>
                </InputAdornment>
              }
            />
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            • Ideal length 50–60 characters • Should include keywords: e.g., "home for sale",
            "Pittsburgh PA" • Use Title Case formatting • End with brand/company name
          </Typography>

          {metaTitleStatus === 'too-short' && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Meta title is too short. Consider adding more keywords for better SEO visibility.
            </Alert>
          )}

          {metaTitleStatus === 'too-long' && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Meta title is too long. Max recommended length is 60 characters.
            </Alert>
          )}
        </Grid>

        {/* META DESCRIPTION */}
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="meta-description-field">Meta Description *</InputLabel>
            <OutlinedInput
              id="meta-description-field"
              value={metaDescription}
              onChange={handleMetaDescriptionChange}
              label="Meta Description *"
              multiline
              rows={3}
              sx={{
                backgroundColor: isDefaultDescription ? '#fff5f5' : 'transparent',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDefaultDescription ? '#ffcdd2' : undefined
                }
              }}
              endAdornment={
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {metaDescriptionStatus === 'ideal' && (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                    {metaDescriptionStatus === 'too-short' && (
                      <ErrorIcon color="warning" fontSize="small" />
                    )}
                    {metaDescriptionStatus === 'too-long' && (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="caption"
                      color={
                        metaDescriptionStatus === 'ideal'
                          ? 'success.main'
                          : metaDescriptionStatus === 'too-short'
                          ? 'warning.main'
                          : metaDescriptionStatus === 'too-long'
                          ? 'error.main'
                          : 'text.secondary'
                      }
                    >
                      {metaDescriptionLength}/160
                    </Typography>
                  </Box>
                </InputAdornment>
              }
            />
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            • Length 120–160 characters • Mention property type + city + state • Include key
            features • Use action words like "Schedule a tour today"
          </Typography>

          {isDefaultDescription && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Using auto-generated description. Please review and edit for better SEO results.
            </Alert>
          )}

          {metaDescriptionStatus === 'too-short' && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Meta description is too short. Minimum 120 characters recommended.
            </Alert>
          )}

          {metaDescriptionStatus === 'too-long' && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Meta description is too long. Max 160 characters recommended.
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SeoTab;
